/**
 * Shared primitive schemas: identifiers, timestamps, units, pagination and errors.
 *
 * Physical quantities carry their unit in the field name (`massKg`,
 * `altitudeMeters`) at the API boundary, even though the physics core works in
 * bare SI. The redundancy is deliberate: an API consumer reading JSON has no type
 * system to tell them, and "altitude: 250000" is ambiguous enough to have caused
 * real mission failures.
 */

import { z } from 'zod';

/** Opaque resource identifier. */
export const IdSchema = z.string().min(1).max(64);
export type Id = z.infer<typeof IdSchema>;

/** ISO-8601 timestamp. */
export const TimestampSchema = z.iso.datetime();
export type Timestamp = z.infer<typeof TimestampSchema>;

/** Fields every persisted resource carries. */
export const AuditFieldsSchema = z.object({
  createdAt: TimestampSchema,
  updatedAt: TimestampSchema,
  /**
   * Monotonic revision counter, used for optimistic concurrency. Clients echo it
   * back in `If-Match`; a mismatch means somebody else changed the record.
   */
  version: z.number().int().nonnegative(),
});
export type AuditFields = z.infer<typeof AuditFieldsSchema>;

/** A 3-vector in metres or metres per second, depending on context. */
export const Vec3Schema = z.tuple([z.number(), z.number(), z.number()]);
export type Vec3Dto = z.infer<typeof Vec3Schema>;

/** Geodetic position. Angles are degrees at the API boundary, radians internally. */
export const GeodeticSchema = z.object({
  latitudeDeg: z.number().min(-90).max(90),
  longitudeDeg: z.number().min(-180).max(180),
  altitudeMeters: z.number(),
});
export type GeodeticDto = z.infer<typeof GeodeticSchema>;

/** Classical orbital elements, in degrees and metres. */
export const OrbitalElementsSchema = z.object({
  semiMajorAxisMeters: z.number(),
  eccentricity: z.number().min(0),
  inclinationDeg: z.number().min(0).max(180),
  raanDeg: z.number().min(0).max(360),
  argumentOfPeriapsisDeg: z.number().min(0).max(360),
  trueAnomalyDeg: z.number().min(0).max(360),
});
export type OrbitalElementsDto = z.infer<typeof OrbitalElementsSchema>;

/** Cursor pagination request. */
export const PaginationSchema = z.object({
  /** Opaque cursor from a previous response. */
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
});
export type Pagination = z.infer<typeof PaginationSchema>;

/** Cursor pagination envelope. */
export const paginated = <T extends z.ZodTypeAny>(item: T) =>
  z.object({
    items: z.array(item),
    /** Cursor for the next page, or null at the end. */
    nextCursor: z.string().nullable(),
    /** Total count when cheap to compute, otherwise null. */
    total: z.number().int().nonnegative().nullable(),
  });

/**
 * Error body, following RFC 9457 (`application/problem+json`).
 *
 * A single machine-readable error shape across every endpoint means the client has
 * one error path rather than one per route.
 */
export const ProblemSchema = z.object({
  /** URI reference identifying the problem type. */
  type: z.string(),
  /** Short human-readable summary. */
  title: z.string(),
  /** HTTP status code. */
  status: z.number().int(),
  /** Human-readable explanation specific to this occurrence. */
  detail: z.string().optional(),
  /** URI reference identifying the specific occurrence. */
  instance: z.string().optional(),
  /** Field-level validation failures, when the problem is a bad request. */
  errors: z
    .array(
      z.object({
        path: z.string(),
        message: z.string(),
      }),
    )
    .optional(),
});
export type Problem = z.infer<typeof ProblemSchema>;

/** Mission classifications offered in the mission editor. */
export const MissionTypeSchema = z.enum([
  'suborbital',
  'leo',
  'meo',
  'geo',
  'lunar',
  'interplanetary',
  'escape',
]);
export type MissionType = z.infer<typeof MissionTypeSchema>;

export const CrewKindSchema = z.enum(['cargo', 'crew']);
export type CrewKind = z.infer<typeof CrewKindSchema>;

export const PropellantTypeSchema = z.enum([
  'lox-kerosene',
  'lox-methane',
  'lox-hydrogen',
  'hypergolic',
  'solid',
  'monopropellant',
  'cold-gas',
  'electric',
]);
export type PropellantTypeDto = z.infer<typeof PropellantTypeSchema>;

export const EngineCycleSchema = z.enum([
  'gas-generator',
  'staged-combustion',
  'full-flow-staged-combustion',
  'expander',
  'pressure-fed',
  'electric-pump',
  'solid',
  'ion',
]);
export type EngineCycleDto = z.infer<typeof EngineCycleSchema>;

export const GravityFidelitySchema = z.enum(['point-mass', 'j2', 'j4']);
export type GravityFidelityDto = z.infer<typeof GravityFidelitySchema>;

export const BodyIdSchema = z.enum(['earth', 'moon', 'sun', 'mars', 'venus', 'jupiter']);
export type BodyIdDto = z.infer<typeof BodyIdSchema>;
